<template>
  <div
    :key="refreshKey"
    class="flex flex-col items-center w-full gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl shadow-blue-gray-900/5 bg-clip-border"
  >
    <VideoDropdownModal v-model:open="showModal" :video-url="videoURL" />
    <div
      class="relative flex flex-col w-full font-regular p-8 portrait:px-2 portrait:py-5 py-10 text-gray-900 dark:text-gray-200 max-w-5xl"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5 mb-6 items-center portrait:items-start"
      >
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-start justify-start landscape:mr-2"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-base dark:text-gray-200"></i>
          </button>
          <div>
            <h3
              class="text-[clamp(18px,2vw,24px)] font-medium dark:text-gray-200"
            >
              General Settings
            </h3>
            <p
              class="text-[14px] font-normal leading-6 text-gray-800 opacity-80 dark:text-gray-400"
            >
              This settings is crucial for store functionality
            </p>
          </div>
        </div>
        <button
          @click="showModal = true"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i
          ><span>Watch Video Tutorial</span>
        </button>
      </nav>
      <template v-if="loadingGeneralDetails">
        <div
          class="w-full flex flex-col justify-center items-center h-20 mt-6 mb-16"
        >
          <LoadingComponent /></div
      ></template>
      <div
        v-else
        class="w-full border-[1px] bg-white dark:bg-gray-700 border-spacing-2 border-gray-900/10 flex flex-col h-full rounded-lg relative"
      >
        <h4
          class="text-gray-800 bg-gray-10 p-6 inline-flex rounded-t-lg text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
        >
          Basic Information
        </h4>
        <form class="flex flex-col gap-4 mt-4 py-6">
          <div class="mb-4 px-6">
            <div
              class="w-full flex flex-col items-start justify-center p-2 pt-3 rounded-lg border dark:border-blue-gray-300 border-blue-gray-50 gap-3"
            >
              <div
                class="mb-4 px-6 w-full"
                :class="{
                  'border-l border-sub-900': editedFields.physicalAddressValue,
                }"
              >
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Physical Shop Address (Optional)
                </p>
                <div class="relative h-14 w-full">
                  <input
                    type="text"
                    placeholder="Your shop or office address"
                    v-model="storeFormData['physicalAddressValue']"
                    @input="
                      (e) =>
                        handleEditedField(
                          e.target.value.trim() !== '',
                          'physicalAddressValue'
                        )
                    "
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        shopAddressError,
                    }"
                  />
                </div>
              </div>
              <div
                class="w-full flex flex-col landscape:flex-row items-start justify-center gap-3"
              >
                <div
                  class="mb-4 px-6 w-full"
                  :class="{
                    'border-l border-sub-900': editedFields.storeCountryValue,
                  }"
                >
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Country
                    <span class="text-red-900 text-[14px] dark:text-red-500"
                      >*</span
                    >
                  </p>
                  <div class="relative h-14 w-full">
                    <select
                      @focusin="handleFocus('country')"
                      @focusout="handleBlur('country')"
                      v-model="storeCountry"
                      :class="{
                        'placeholder:text-slate-400 text-slate-700 border-slate-200  ease focus:border-slate-400     w-full h-14 border border-blue-gray-200 dark:border-gray-600 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-100 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 text-sm rounded-lg px-3 py-2.5 duration-300 ease focus:outline-none hover:border-gray-900/60 dark:hover:border-gray-100 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-100 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 appearance-none cursor-pointer !pr-9': true,
                        'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                          storeCountryError,
                      }"
                    >
                      <!-- Disabled placeholder option -->
                      <option
                        disabled
                        value=""
                        class="text-blue-gray-200 dark:text-gray-400"
                      >
                        {{
                          storeFormData.storeCountryValue || "Select a country"
                        }}
                      </option>

                      <!-- Dynamic country options -->
                      <option
                        v-for="country in countries"
                        :key="country.code"
                        :value="country"
                        class="text-blue-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                      >
                        {{ country.name }}
                      </option>
                    </select>
                    <div
                      class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-3 -translate-y-2/4"
                    >
                      <i
                        :class="{
                          'fa-solid fa-chevron-down transition-transform duration-300 h-5 w-5 font-bold': true,
                          'transform rotate-180': isCountryFocused,
                        }"
                      ></i>
                    </div>
                  </div>
                </div>
                <div
                  class="mb-4 px-6 w-full"
                  :class="{
                    'border-l border-sub-900': editedFields.storeStateValue,
                  }"
                >
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    State
                    <span class="text-red-900 text-[14px] dark:text-red-500"
                      >*</span
                    >
                  </p>
                  <div class="relative h-14 w-full">
                    <select
                      @focusin="handleFocus('state')"
                      @focusout="handleBlur('state')"
                      v-model="storeState"
                      :class="{
                        'w-full h-14 border border-blue-gray-200 dark:border-gray-600 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-100 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 text-sm rounded-lg px-3 py-2.5 duration-300 ease focus:outline-none hover:border-gray-900/60 dark:hover:border-gray-100 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-100 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 appearance-none cursor-pointer !pr-9': true,
                        'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                          storeStateError,
                      }"
                    >
                      <!-- Disabled placeholder option -->
                      <option
                        value=""
                        class="text-blue-gray-200 dark:text-gray-400"
                        disabled
                      >
                        {{ storeFormData.storeStateValue || "Select a state" }}
                      </option>

                      <!-- Dynamic state options -->
                      <option
                        v-for="state in storeStates"
                        :key="state.code"
                        :value="state"
                        class="text-blue-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                      >
                        {{ state.name }}
                      </option>
                    </select>
                    <div
                      class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-3 -translate-y-2/4"
                    >
                      <i
                        :class="{
                          'fa-solid fa-chevron-down transition-transform duration-300 h-5 w-5 font-bold': true,
                          'transform rotate-180': isStateFocused,
                        }"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="w-full flex flex-col landscape:flex-row items-start justify-center gap-3"
              >
                <div
                  class="mb-4 px-6 w-full"
                  :class="{
                    'border-l border-sub-900': editedFields.storeWhatsappValue,
                  }"
                >
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Active WhatsApp<span
                      class="text-red-900 text-[14px] dark:text-red-500"
                      >*</span
                    >
                  </p>
                  <div class="relative h-14 w-full">
                    <input
                      type="tel"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      v-model="storeFormData['storeWhatsappValue']"
                      @focus="
                        (e) => {
                          const code = getPhoneCode(
                            storeCountry.name || storeFormData.storeCountryValue
                          );
                          if (
                            storeFormData.storeWhatsappValue.startsWith(code)
                          ) {
                            storeFormData.storeWhatsappValue =
                              storeFormData.storeWhatsappValue.slice(
                                code.length
                              );
                          }
                        }
                      "
                      @input="
                        (e) => {
                          handleEditedField(
                            e.target.value.trim() !== '',
                            'storeWhatsappValue'
                          );
                        }
                      "
                      @blur="
                        (e) => {
                          const code = getPhoneCode(
                            storeCountry.name || storeFormData.storeCountryValue
                          );
                          let digitsOnly = e.target.value.replace(/\D/g, '');
                          if (digitsOnly.startsWith('0')) {
                            digitsOnly = digitsOnly.slice(1); // remove leading 0
                          }
                          digitsOnly
                            ? (storeFormData.storeWhatsappValue =
                                code + digitsOnly)
                            : '';
                          handleEditedField(
                            e.target.value.trim() !== '',
                            'storeWhatsappValue'
                          );
                          whatsAppNumberError = !regex.test(
                            storeFormData.storeWhatsappValue
                          );
                        }
                      "
                      placeholder="Whatsapp number"
                      :class="{
                        'peer no-spinner h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                        'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                          whatsAppNumberError,
                      }"
                    />
                  </div>
                </div>
                <div
                  class="mb-4 px-6 w-full"
                  :class="{
                    'border-l border-sub-900': editedFields.storeCurrencyValue,
                  }"
                >
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Currency
                    <span class="text-red-900 text-[14px] dark:text-red-500"
                      >*</span
                    >
                  </p>
                  <div class="relative h-14 w-full">
                    <select
                      @focusin="handleFocus('category')"
                      @focusout="handleBlur('category')"
                      v-model="storeCurrency"
                      :class="{
                        'w-full h-14 border border-blue-gray-200 dark:border-gray-600 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-100 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 text-sm rounded-lg px-3 py-2.5 duration-300 ease focus:outline-none hover:border-gray-900/60 dark:hover:border-gray-100 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-100 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 appearance-none cursor-pointer !pr-9': true,
                        'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                          storeCurrencyError,
                      }"
                    >
                      <!-- Disabled placeholder option -->
                      <option
                        disabled
                        value=""
                        class="text-blue-gray-200 dark:text-gray-400"
                      >
                        {{
                          getCurrencyCode(storeFormData.storeCurrencyValue) ||
                          "Select currency"
                        }}
                      </option>

                      <!-- Dynamic currency options -->
                      <option
                        v-for="currency in currencies"
                        :key="currency.id"
                        :value="currency.id"
                        class="text-blue-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                      >
                        {{ currency.currency_code }}
                      </option>
                    </select>
                    <div
                      class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-3 -translate-y-2/4"
                    >
                      <i
                        :class="{
                          'fa-solid fa-chevron-down transition-transform duration-300 h-5 w-5 font-bold': true,
                          'transform rotate-180': isCategoryFocused,
                        }"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            class="mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeVisibilityValue,
            }"
          >
            <div class="flex items-center justify-between">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Under Construction
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable this when you are actively working on your store.
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="visibilitySwitch"
                  type="checkbox"
                  v-model="storeFormData.storeVisibilityValue"
                  @change="handleEditedField(true, 'storeVisibilityValue')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                />
                <label
                  htmlFor="visibilitySwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 bg-gray-100 p-6 inline-flex text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
          >
            Social Media
          </h4>
          <div
            class="mt-4 mb-1 px-6"
            :class="{
              'border-l border-sub-900': editedFields.socialAccounts,
            }"
          >
            <!-- Label -->
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Social accounts (Optional)
            </p>

            <!-- Social Accounts Container -->
            <div
              class="w-full flex flex-col items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-gray-600 gap-3"
            >
              <!-- Social Type and Input -->
              <div class="w-full px-4">
                <div class="relative h-14 w-full">
                  <!-- Social Type Dropdown -->
                  <div class="absolute left-0 inset-y-0 flex items-center pl-3">
                    <button
                      type="button"
                      @click="toggleSocialDropDown"
                      class="h-full flex justify-center gap-1 items-center text-sm outline-none"
                      :class="
                        !!selectedSocial
                          ? 'text-blue-gray-800 dark:text-gray-200'
                          : 'text-gray-800/60 dark:text-gray-400'
                      "
                    >
                      {{ selectedSocial?.name ?? "Social Type" }}
                      <i
                        class="fa-solid fa-chevron-down transition-transform duration-300"
                        :class="socialDropdownOpen ? 'rotate-90' : 'rotate-0'"
                      ></i>
                    </button>
                    <div
                      class="h-6 border-l border-blue-gray-200 dark:border-gray-600 ml-2"
                    ></div>
                    <!-- Dropdown Menu -->
                    <div
                      id="dropdownMenu"
                      class="min-w-[150px] overflow-hidden absolute left-0 w-full mt-10 bg-white dark:bg-gray-800 border border-blue-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 p-4"
                      :class="socialDropdownOpen ? 'block' : 'hidden'"
                    >
                      <ul>
                        <li
                          v-for="account in socialMediaAccounts"
                          :key="account.id"
                          class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                          @click="socialOption(account)"
                        >
                          {{ account.name }}
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Add Button -->
                  <div class="p-1 absolute right-0 inset-y-0">
                    <button
                      @click="addSocial"
                      type="button"
                      class="bg-blue-gray-50 dark:bg-gray-700 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-200 flex gap-1 items-center justify-center"
                    >
                      <i class="fa-solid fa-plus"></i>Add
                    </button>
                  </div>

                  <!-- Username Input -->
                  <input
                    type="text"
                    placeholder="@username"
                    v-model="selectedSocialValue"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-[85px] pl-[124px]': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        socialError,
                    }"
                  />
                </div>
              </div>

              <!-- Added Social Accounts -->
              <div class="w-full flex flex-wrap px-4 gap-1">
                <span
                  v-for="(account, index) in storeFormData.socialAccounts"
                  :key="index"
                  class="w-max py-2 pr-8 pl-2 bg-blue-gray-50 dark:bg-gray-50/20 rounded-lg text-blue-gray-800 dark:text-gray-200 text-sm text-center items-center justify-center relative"
                >
                  <a
                    :href="account?.link"
                    class="hover:text-main-600 dark:hover:text-main-400"
                  >
                    {{ account.socialMedia }} : {{ account.handle }}
                  </a>
                  <button
                    @click="deleteSocial(index)"
                    type="button"
                    class="!absolute p-2 h-6 w-6 top-1 right-1 hover:text-main-600 dark:hover:text-sub-600 text-main-900 dark:text-sub-900 flex items-center justify-center z-[9]"
                  >
                    <i class="fa-solid fa-trash text-[14px]"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 bg-gray-100 p-6 inline-flex text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
          >
            V.A.T
          </h4>
          <div
            class="mt-4 mb-1 px-6"
            :class="{
              'border-l border-sub-900': editedFields.storeVat,
            }"
          >
            <div class="flex items-center justify-between mb-2">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Enable VAT
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable this to charge your users tax on every purchase
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="vatSwitch"
                  type="checkbox"
                  v-model="storeFormData.storeVat.status"
                  @input="handleEditedField(true, 'storeVat')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                />
                <label
                  htmlFor="vatSwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div
              v-if="storeFormData.storeVat.status"
              class="w-full flex flex-col items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3"
            >
              <div class="w-full p-2">
                <div class="relative w-full h-14">
                  <span
                    class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-4 font-bold -translate-y-2/4"
                    >%</span
                  >
                  <input
                    type="text"
                    v-model="storeFormData.storeVat.value"
                    @input="handleEditedField(true, 'storeVat')"
                    placeholder="7.5"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-10': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        storeVatError,
                    }"
                  />
                </div>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 bg-gray-100 p-6 inline-flex text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
          >
            Payment Setup
          </h4>
          <div
            class="mt-4 mb-1 px-6"
            :class="{
              'border-l border-sub-900': editedFields.paystack,
            }"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Paystack
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable this option to accept payment using Paystack
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="paystackSwitch"
                  type="checkbox"
                  v-model="storeFormData.paystack.status"
                  @input="handleEditedField(true, 'paystack')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                />
                <label
                  htmlFor="paystackSwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div
              v-if="storeFormData.paystack.status"
              class="w-full flex flex-col landscape:flex-row items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3"
            >
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Paystack Key
                </p>
                <div class="relative h-14 w-full">
                  <input
                    type="text"
                    v-model="storeFormData.paystack.key"
                    @input="handleEditedField(true, 'paystack')"
                    placeholder="pk_live_00000000000000000000000"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        paystackKeyError,
                    }"
                  />
                </div>
              </div>
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Paystack Secret
                </p>
                <div class="relative h-14 w-full">
                  <input
                    :type="showPaystackSecret ? 'text' : 'password'"
                    v-model="storeFormData.paystack.secret"
                    @input="handleEditedField(true, 'paystack')"
                    placeholder="sk_live_00000000000000000000000"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-14': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        paystackSecretError,
                    }"
                  />
                  <button
                    v-if="isAdmin && storeFormData.paystack.secret"
                    type="button"
                    class="absolute right-3 top-2/4 -translate-y-2/4 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    @click="showPaystackSecret = !showPaystackSecret"
                    tabindex="-1"
                  >
                    <i
                      :class="
                        showPaystackSecret
                          ? 'fa-solid fa-eye-slash'
                          : 'fa-solid fa-eye'
                      "
                    ></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            class="mt-4 mb-1 px-6"
            :class="{ 'border-l border-sub-900': editedFields.hisellPay }"
          >
            <!-- Header with toggle -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  HiSell Pay
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable to let HiSell process payments for you
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="hisellPaySwitch"
                  type="checkbox"
                  v-model="storeFormData.hisellPay.status"
                  @input="handleEditedField(true, 'hisellPay')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                />
                <label
                  htmlFor="hisellPaySwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>

            <!-- Fee Information Box -->
            <div
              v-if="storeFormData.hisellPay.status"
              class="mb-4 p-4 bg-blue-50 dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-gray-700"
            >
              <div class="flex items-start gap-2">
                <i
                  class="fa-solid fa-circle-info text-gray-600 dark:text-gray-400 mt-1"
                ></i>
                <div>
                  <p class="font-medium text-gray-800 dark:text-gray-200 mb-2">
                    Payment Processing Fees
                  </p>
                  <ul
                    class="text-sm text-gray-700 dark:text-gray-300 space-y-1"
                  >
                    <li class="flex items-baseline gap-1">
                      <span>• HiSell fee:</span>
                      <span class="font-medium">1.7%</span>
                      <span>per transaction</span>
                    </li>
                    <li class="flex items-baseline gap-1">
                      <span>• Paystack fee:</span>
                      <span class="font-medium">1.5%</span>
                      <span>(for local transactions)</span>
                    </li>
                    <li class="pt-2 text-gray-800 dark:text-gray-200">
                      Total estimated fee: ~3.2% per transaction
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Bank Account Form -->
            <div
              v-if="storeFormData.hisellPay.status"
              class="w-full flex flex-col landscape:flex-row items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3"
            >
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Bank/Account No
                </p>
                <div class="relative h-14 w-full">
                  <select
                    v-model="storeFormData.hisellPay.bank"
                    @change="handleEditedField(true, 'hisellPay')"
                    class="absolute left-0 top-0 w-28 h-14 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-200 outline-none transition-all text-sm duration-300 ease cursor-pointer pl-2 border-r border-blue-gray-200 dark:border-gray-600"
                  >
                    <option
                      disabled
                      value=""
                      class="text-blue-gray-200 dark:text-gray-400"
                    >
                      Select Bank
                    </option>
                    <option
                      v-for="bank in banks"
                      :key="bank.id"
                      :value="bank.name"
                      class="text-blue-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800"
                    >
                      {{ bank.name }}
                    </option>
                  </select>
                  <input
                    type="text"
                    placeholder="0100450032"
                    @input="handleEditedField(true, 'hisellPay')"
                    v-model="storeFormData.hisellPay.account"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pl-32': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        hisellPayAcccountError,
                    }"
                  />
                </div>
              </div>
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Account Name
                </p>
                <div class="relative h-14 w-full">
                  <input
                    v-if="!hisellPayNameLoading"
                    type="text"
                    disabled
                    v-model="storeFormData.hisellPay.name"
                    @input="handleEditedField(true, 'hisellPay')"
                    placeholder="Oluade Simon Example"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-gray-300 px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 dark:bg-gray-800 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:opacity-60 disabled:cursor-not-allowed pr-24': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        hisellPayNameError,
                    }"
                  />
                  <div
                    v-else
                    class="flex items-center justify-center h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 bg-transparent text-blue-gray-800 dark:text-gray-200"
                  >
                    <i class="fa-solid fa-spinner fa-spin mr-2"></i> Fetching
                    account name...
                  </div>
                  <div class="p-1 absolute right-0 inset-y-0">
                    <button
                      v-if="
                        storeFormData.hisellPay.name &&
                        !storeFormData.hisellPay.subAccountCreated
                      "
                      @click="createSubAccount"
                      type="button"
                      class="bg-blue-gray-50 dark:bg-gray-700 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-200 flex gap-1 items-center justify-center"
                    >
                      <i class="fa-solid fa-plus"></i>Add
                    </button>
                    <div
                      v-else-if="storeFormData.hisellPay.subAccountCreated"
                      class="bg-green-500 text-white rounded-r-lg px-4 py-3 h-full flex items-center justify-center"
                    >
                      <i class="fa-solid fa-check"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Verification Notice -->
            <div
              v-if="
                storeFormData.hisellPay.status &&
                !storeFormData.hisellPay.subAccountCreated
              "
              class="mt-3 p-3 bg-yellow-50 dark:bg-gray-800 rounded-lg border border-yellow-200 dark:border-gray-700"
            >
              <div class="flex items-start gap-2">
                <i
                  class="fa-solid fa-clock text-yellow-600 dark:text-yellow-400 mt-0.5"
                ></i>
                <div>
                  <p class="text-sm text-yellow-800 dark:text-yellow-200">
                    After adding your account, ensure you save below before
                    exit. Our team will verify your account within 24 hours.
                  </p>
                </div>
              </div>
            </div>

            <!-- Verification Success Message -->
            <div
              v-if="storeFormData.hisellPay.subAccountCreated"
              class="mt-3 p-3 bg-green-50 dark:bg-gray-800 rounded-lg border border-green-200 dark:border-gray-700"
            >
              <div
                class="flex items-center gap-2 text-green-700 dark:text-green-300"
              >
                <i class="fa-solid fa-circle-check"></i>
                <p class="text-sm">
                  Your account is verified and ready to receive payments!
                </p>
              </div>
            </div>
          </div>
          <div
            class="mt-4 mb-4 px-6"
            :class="{
              'border-l border-sub-900': editedFields.whatsAppOrder,
            }"
          >
            <div class="flex items-center justify-between mb-4">
              <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  WhatsApp Order
                </p>
                <div class="flex flex-row justify-center items-start gap-1">
                  <i
                    class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                  ></i>
                  <p class="text-sm">
                    Enable this option to take orders directly on your WhatsApp.
                  </p>
                </div>
              </div>
              <div
                class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
              >
                <input
                  id="whatsAppOrderSwitch"
                  type="checkbox"
                  v-model="storeFormData.whatsAppOrder.status"
                  @input="handleEditedField(true, 'whatsAppOrder')"
                  class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                />
                <label
                  htmlFor="whatsAppOrderSwitch"
                  class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                >
                  <div
                    class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    data-ripple-dark="true"
                  ></div>
                </label>
              </div>
            </div>
            <div
              v-if="storeFormData.whatsAppOrder.status"
              class="w-full flex flex-col items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3"
            >
              <div class="mb-1 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  WhatsApp number
                </p>
                <div class="relative h-14 w-full">
                  <input
                    type="text"
                    v-model="storeFormData.whatsAppOrder.number"
                    @input="handleEditedField(true, 'whatsAppOrder')"
                    placeholder="2340000000000"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        whatsAppOrderNumberError,
                    }"
                  />
                </div>
              </div>
              <div class="mb-4 px-6 w-full flex items-center gap-2">
                <p
                  class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Use same as above
                </p>
                <label
                  class="relative flex items-center rounded-full cursor-pointer"
                  htmlFor="link"
                >
                  <input
                    type="checkbox"
                    @input="handleEditedField(true, 'whatsAppOrder')"
                    @change="toggleWhatsAppNumber"
                    class="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-main-400 dark:border-sub-400 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-main-500 dark:before:bg-sub-500 before:opacity-0 before:transition-opacity checked:border-main-900 checked:bg-main-900 checked:before:bg-main-900 dark:checked:border-sub-900 dark:checked:bg-sub-900 dark:checked:before:bg-sub-900 hover:before:opacity-10"
                    id="link"
                  />
                  <span
                    class="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100"
                  >
                    <i class="fa-solid fa-check"></i>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <!--SHIPPING STARTS HERE -->
          <h4
            class="text-gray-800 bg-gray-100 p-6 inline-flex text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
          >
            Shipping Details
          </h4>
          <div
            class="mt-4 mb-1 px-6"
            :class="{
              'border-l border-sub-900': editedFields.deliveryLocations,
            }"
          >
            <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
              <p
                class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
              >
                Delivery Locations
              </p>
              <div class="flex flex-row justify-center items-start gap-1">
                <i
                  class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                ></i>
                <p class="text-sm">Set delivery locations with cost</p>
              </div>
            </div>
            <div
              class="w-full flex flex-col landscape:flex-row items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3"
            >
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Delivery Country
                </p>
                <div class="relative h-14 w-full">
                  <select
                    @focusin="handleFocus('delivery')"
                    @focusout="handleBlur('delivery')"
                    v-model="deliveryCountry"
                    :class="{
                      'w-full h-14 border border-blue-gray-200 dark:border-gray-600 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-100 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 text-sm rounded-lg px-3 py-2.5 duration-300 ease focus:outline-none hover:border-gray-900/60 dark:hover:border-gray-100 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-100 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 appearance-none cursor-pointer !pr-9': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        deliveryLocationError,
                    }"
                  >
                    <!-- Disabled placeholder option -->
                    <option
                      disabled
                      value=""
                      class="text-blue-gray-200 dark:text-gray-400"
                    >
                      Select a country
                    </option>

                    <!-- Dynamic country options -->
                    <option
                      v-for="country in countries"
                      :key="country.code"
                      :value="country"
                      class="text-blue-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                    >
                      {{ country.name }}
                    </option>
                  </select>
                  <div
                    class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-3 -translate-y-2/4"
                  >
                    <i
                      :class="{
                        'fa-solid fa-chevron-down transition-transform duration-300 h-5 w-5 font-bold': true,
                        'transform rotate-180': isDeliveryFocused,
                      }"
                    ></i>
                  </div>
                </div>
              </div>
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Delivery Cost
                </p>
                <div class="relative w-full h-14">
                  <div
                    :class="{
                      'absolute flex flex-col justify-center items-center w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 left-3 -translate-y-2/4': true,
                      '!text-red-800/80': deliveryError,
                    }"
                  >
                    <span class="text-lg font-medium">{{
                      currencySymbol
                    }}</span>
                  </div>
                  <div class="p-1 absolute right-0 inset-y-0">
                    <button
                      @click="addDeliveryLocation"
                      type="button"
                      class="bg-blue-gray-50 dark:bg-gray-700 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-200 flex gap-1 items-center justify-center"
                    >
                      <i class="fa-solid fa-plus"></i>Add
                    </button>
                  </div>
                  <input
                    :disabled="addingStateException"
                    type="number"
                    placeholder="0.00"
                    v-model="deliveryCost"
                    :class="{
                      'peer no-spinner h-full w-full rounded-lg border border-blue-gray-200 border-t-transparent !border-t-blue-gray-200 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-gray-900 focus:border-t-transparent focus:!border-t-gray-900 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 !pl-9 pr-[90px]': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        deliveryError,
                    }"
                  />
                </div>
              </div>
            </div>
            <div
              class="py-2 w-full flex items-center"
              v-if="!addingStateException"
            >
              <button
                class="rounded-md border border-transparent py-2 px-4 flex items-center text-center text-sm transition-all text-blue-gray-600 dark:text-sub-600 hover:bg-blue-gray-100 dark:hover:bg-gray-50/20 focus:bg-blue-gray-100 active:bg-blue-gray-100 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
                type="button"
                @click="addstateException"
              >
                Add State Exceptions
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="w-4 h-4 ml-1.5"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.72 7.72a.75.75 0 0 1 1.06 0l3.75 3.75a.75.75 0 0 1 0 1.06l-3.75 3.75a.75.75 0 1 1-1.06-1.06l2.47-2.47H3a.75.75 0 0 1 0-1.5h16.19l-2.47-2.47a.75.75 0 0 1 0-1.06Z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div
              v-else
              class="w-full flex flex-col landscape:flex-row items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3 mt-1"
            >
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  State
                </p>
                <div class="relative h-14 w-full">
                  <select
                    @focusin="handleFocus('stateexception')"
                    @focusout="handleBlur('stateexception')"
                    v-model="deliveryState"
                    :class="{
                      'w-full h-14 border border-blue-gray-200 dark:border-gray-600 bg-transparent placeholder:text-gray-800/60 dark:placeholder:text-gray-400 text-blue-gray-800 dark:text-gray-100 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 text-sm rounded-lg px-3 py-2.5 duration-300 ease focus:outline-none hover:border-gray-900/60 dark:hover:border-gray-100 focus:border-2 focus:border-gray-900 dark:focus:border-gray-100 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-100 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 appearance-none cursor-pointer !pr-9': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        statedeliveryLocationError,
                    }"
                  >
                    <!-- Disabled placeholder option -->
                    <option
                      disabled
                      value=""
                      class="text-blue-gray-200 dark:text-gray-400"
                    >
                      Select a state
                    </option>

                    <!-- Dynamic state options -->
                    <option
                      v-for="state in deliveryStates"
                      :key="state.code"
                      :value="state.name"
                      class="text-blue-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
                    >
                      {{ state.name }}
                    </option>
                  </select>
                  <div
                    class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-3 -translate-y-2/4"
                  >
                    <i
                      :class="{
                        'fa-solid fa-chevron-down transition-transform duration-300 h-5 w-5 font-bold': true,
                        'transform rotate-180': isDeliveryStateFocused,
                      }"
                    ></i>
                  </div>
                </div>
              </div>
              <div class="mb-4 px-6 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Delivery Cost
                </p>
                <div class="relative w-full h-14">
                  <div
                    :class="{
                      'absolute flex flex-col justify-center items-center w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 left-3 -translate-y-2/4': true,
                      '!text-red-800/80': statedeliveryError,
                    }"
                  >
                    <span class="text-lg font-medium">{{
                      currencySymbol
                    }}</span>
                  </div>
                  <div class="p-1 absolute right-0 inset-y-0">
                    <div class="flex items-center gap-1 h-full justify-center">
                      <button
                        @click="addDeliveryLocationState"
                        type="button"
                        class="bg-blue-gray-50 rounded-lg px-4 py-3 h-full hover:bg-blue-gray-100 text-blue-gray-800 flex gap-1 items-center"
                      >
                        <i class="fa-solid fa-plus"></i>
                      </button>
                      <button
                        @click="addingStateException = false"
                        type="button"
                        class="bg-red-50 rounded-lg px-4 py-3 h-full hover:bg-red-100 text-red-600 flex gap-1 items-center"
                      >
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                  <input
                    type="number"
                    placeholder="0.00"
                    v-model="statedeliveryCost"
                    :class="{
                      'peer no-spinner h-full w-full rounded-lg border border-blue-gray-200 border-t-transparent !border-t-blue-gray-200 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-gray-900 focus:border-t-transparent focus:!border-t-gray-900 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 !pl-9 pr-[calc(100%-100px)]': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        statedeliveryError,
                    }"
                  />
                </div>
              </div>
            </div>
            <div
              class="w-full flex flex-col landscape:flex-row items-start justify-center p-2 pt-3 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-3 mt-1"
            >
              <div class="w-full flex flex-wrap px-6 gap-1">
                <span
                  v-for="(location, index) in storeFormData.deliveryLocations"
                  :key="index"
                  class="relative inline-flex items-center w-max py-2 pr-8 pl-2 bg-blue-gray-50 dark:bg-gray-50/20 rounded-lg text-blue-gray-800 dark:text-gray-100 text-sm text-center"
                >
                  {{ location.location }} :
                  {{ currencySymbol + " " + location.cost }}

                  <button
                    @click="deleteLocation(index)"
                    type="button"
                    class="absolute p-2 h-6 w-6 top-1 right-1 hover:text-main-600 text-main-900 dark:hover:text-sub-600 dark:text-sub-900 flex items-center justify-center transition duration-300 ease-in-out"
                  >
                    <i class="fa-solid fa-trash text-[14px]"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
          <div class="mt-4 mb-1 px-6">
            <div class="flex flex-col items-start portrait:w-3/4 gap-1 mb-4">
              <p
                class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
              >
                Delivery Options
              </p>
              <div class="flex flex-row justify-center items-start gap-1">
                <i
                  class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                ></i>
                <p class="text-sm">
                  Set applicable delivery options. (Percentage increase are
                  based on set delivery cost)
                </p>
              </div>
            </div>
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-4 mb-4"
              :class="{
                'border-l border-sub-900': editedFields.doorstepDelivery,
              }"
            >
              <div class="flex items-center justify-between mb-2 w-full">
                <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Enable Doorstep Delivery
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                    ></i>
                    <p class="text-sm">
                      Enable this if you offer doorstep delivery
                    </p>
                  </div>
                </div>
                <div
                  class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
                >
                  <input
                    id="doorstepDeliveryswitch"
                    type="checkbox"
                    v-model="storeFormData.doorstepDelivery.status"
                    @input="handleEditedField(true, 'doorstepDelivery')"
                    class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  />
                  <label
                    htmlFor="doorstepDeliveryswitch"
                    class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                  >
                    <div
                      class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                      data-ripple-dark="true"
                    ></div>
                  </label>
                </div>
              </div>
              <div
                v-if="storeFormData.doorstepDelivery.status"
                class="relative w-full h-14"
              >
                <span
                  class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-4 font-bold -translate-y-2/4"
                  >%</span
                >
                <input
                  type="number"
                  placeholder="Default is 20%"
                  v-model="storeFormData.doorstepDelivery.value"
                  @input="handleEditedField(true, 'doorstepDelivery')"
                  :class="{
                    'peer no-spinner h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-10': true,
                    'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                      doorstepDeliveryError,
                  }"
                />
              </div>
            </div>
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-4 mb-4"
              :class="{
                'border-l border-sub-900': editedFields.payonDelivery,
              }"
            >
              <div class="flex items-center justify-between mb-2 w-full">
                <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Enable Pay on Delivery
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                    ></i>
                    <p class="text-sm">
                      Enable this option if you offer pay on delivery
                    </p>
                  </div>
                </div>
                <div
                  class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
                >
                  <input
                    id="payonDeliveryswitch"
                    type="checkbox"
                    v-model="storeFormData.payonDelivery.status"
                    @input="handleEditedField(true, 'payonDelivery')"
                    class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  />
                  <label
                    htmlFor="payonDeliveryswitch"
                    class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                  >
                    <div
                      class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                      data-ripple-dark="true"
                    ></div>
                  </label>
                </div>
              </div>
              <div
                v-if="storeFormData.payonDelivery.status"
                class="relative w-full h-14"
              >
                <span
                  class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-100 top-2/4 right-4 font-bold -translate-y-2/4"
                  >%</span
                >
                <input
                  type="number"
                  v-model="storeFormData.payonDelivery.value"
                  placeholder="Default is 20%"
                  @input="handleEditedField(true, 'payonDelivery')"
                  :class="{
                    'peer no-spinner h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-10': true,
                    'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                      payonDeliveryError,
                  }"
                />
              </div>
            </div>
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-4 mb-4"
              :class="{
                'border-l border-sub-900': editedFields.parkPickup,
              }"
            >
              <div class="flex items-center justify-between mb-2 w-full">
                <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Enable Pickup at park
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                    ></i>
                    <p class="text-sm">
                      Enable this option if you deliver using local park drivers
                      and direct users to pick up from park.
                    </p>
                  </div>
                </div>
                <div
                  class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
                >
                  <input
                    id="parkPickupSwitch"
                    type="checkbox"
                    v-model="storeFormData.parkPickup"
                    @input="handleEditedField(true, 'parkPickup')"
                    class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  />
                  <label
                    htmlFor="parkPickupSwitch"
                    class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                  >
                    <div
                      class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                      data-ripple-dark="true"
                    ></div>
                  </label>
                </div>
              </div>
            </div>
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50 dark:border-blue-gray-300 gap-4 mb-4"
              :class="{
                'border-l border-sub-900': editedFields.storePickup,
              }"
            >
              <div class="flex items-center justify-between mb-2 w-full">
                <div class="flex flex-col items-start portrait:w-3/4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Enable Physical Pickup (FREE)
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation text-main-800 dark:text-sub-800"
                    ></i>
                    <p class="text-sm">
                      Enable this option if you have a pickup location a
                      physical store/warehouse
                    </p>
                  </div>
                </div>
                <div
                  class="relative inline-block w-8 h-6 rounded-full cursor-pointer"
                >
                  <input
                    id="storePickupSwitch"
                    type="checkbox"
                    v-model="storeFormData.storePickup"
                    @input="handleEditedField(true, 'storePickup')"
                    class="absolute w-8 h-6 transition-colors duration-300 rounded-full appearance-none cursor-pointer peer bg-blue-gray-100 checked:bg-main-900 peer-checked:border-main-900 peer-checked:before:bg-main-900 dark:checked:bg-sub-900 dark:peer-checked:border-sub-900 dark:peer-checked:before:bg-sub-900"
                  />
                  <label
                    htmlFor="storePickupSwitch"
                    class="before:content[''] absolute top-2/4 -left-2 h-6 w-6 -translate-y-2/4 cursor-pointer rounded-full border border-blue-gray-100 bg-white shadow-md transition-all duration-300 before:absolute before:top-2/4 before:left-2/4 before:block before:h-10 before:w-10 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity hover:before:opacity-10 peer-checked:translate-x-full peer-checked:border-main-900 peer-checked:before:bg-main-900"
                  >
                    <div
                      class="inline-block p-5 rounded-full top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                      data-ripple-dark="true"
                    ></div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!--ADVANCED INTERGRATION STARTS HERE-->
          <h4
            class="text-gray-800 bg-gray-100 p-6 inline-flex text-[18px] font-medium dark:text-gray-200 dark:bg-gray-600"
          >
            Intergration (Advanced Settings)
          </h4>
          <div class="mt-4 mb-1 px-6">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Header Codes
            </p>
            <div class="relative h-14 w-full">
              <!-- Header Type Dropdown -->
              <div class="absolute left-0 inset-y-0 flex items-center pl-3">
                <button
                  type="button"
                  @click="toggleHeaderCodeDropdown"
                  class="h-full flex justify-center gap-1 items-center text-sm outline-none"
                  :class="
                    !!selectedHeaderType
                      ? 'text-blue-gray-800 dark:text-gray-200'
                      : 'text-gray-800/60 dark:text-gray-400'
                  "
                >
                  {{ selectedHeaderType || "Header type" }}
                  <i
                    class="fa-solid fa-chevron-down transition-transform duration-300"
                    :class="headerCodeDropdownOpen ? 'rotate-90' : 'rotate-0'"
                  ></i>
                </button>
                <div
                  class="h-6 border-l border-blue-gray-200 dark:border-gray-600 ml-2"
                ></div>
                <!-- Dropdown Menu -->
                <div
                  id="dropdownMenu"
                  class="min-w-[240px] overflow-auto max-h-[240px] absolute left-0 w-full mt-10 bg-white dark:bg-gray-800 border border-blue-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 p-4"
                  :class="headerCodeDropdownOpen ? 'block' : 'hidden'"
                >
                  <ul>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('FP')"
                    >
                      Facebook Pixel
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('GTM')"
                    >
                      Google Tag Manager
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('GA4')"
                    >
                      Google Analytics 4
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('Hotjar')"
                    >
                      Hotjar Tracking
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('Pintrest')"
                    >
                      Pinterest
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('LT')"
                    >
                      LinkedIn Tag
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('TP')"
                    >
                      Twitter Pixel
                    </li>
                    <li
                      class="px-4 py-2 text-blue-gray-600 dark:text-gray-300 hover:bg-blue-gray-50 dark:hover:bg-gray-700 text-sm cursor-pointer"
                      @click="headerCodeOption('SOV')"
                    >
                      Site Ownership Verification
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Add Button -->
              <div class="p-1 absolute right-0 inset-y-0">
                <button
                  @click="addHeaderCode"
                  type="button"
                  class="bg-blue-gray-50 dark:bg-gray-700 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-200 flex gap-1 items-center justify-center"
                >
                  <i class="fa-solid fa-plus"></i>Add
                </button>
              </div>

              <!-- Header Code Input -->
              <input
                type="text"
                placeholder="XXXXXXXXXXXXX"
                v-model="selectedHeaderValue"
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-[85px] pl-[124px]': true,
                  'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                    socialError,
                }"
              />
            </div>
          </div>
          <div class="w-full flex flex-wrap px-6 gap-1">
            <span
              v-for="(headerCode, index) in headerCodes"
              :key="index"
              class="max-w-[150px] flex-1 overflow-hidden text-ellipsis whitespace-nowrap py-2 pr-8 pl-2 bg-blue-gray-50 rounded-lg text-blue-gray-800 text-sm text-center items-center justify-center relative"
            >
              {{ headerCode.type }} : {{ headerCode.code }}
              <button
                @click="deleteHeaderCode(index)"
                type="button"
                class="!absolute p-2 h-6 w-6 top-1 right-1 hover:text-main-600 text-main-900 dark:hover:text-sub-600 dark:text-sub-900 flex items-center justify-center"
              >
                <i class="fa-solid fa-trash text-[14px]"></i>
              </button>
            </span>
          </div>
          <div
            v-if="Object.keys(this.editedFields).length > 0"
            class="flex flex-row justify-end items-center gap-4 px-6"
          >
            <button
              @click="handleSubmitForm"
              type="button"
              class="align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-lg portrait:text-sm py-3 px-6 rounded-lg bg-main-800 dark:bg-sub-800 text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none"
            >
              Save <i class="fa-solid fa-floppy-disk text-base ml-2"></i>
            </button>
            <button
              @click="resetForm"
              type="button"
              class="select-none rounded-lg border border-main-900 dark:border-sub-900 py-3 px-4 text-center portrait:text-sm align-middle text-lg font-semibold text-main-900 dark:text-sub-900 transition-all hover:opacity-75 active:opacity-[0.85] disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            >
              Reset <i class="fa-solid fa-rotate-left text-base ml-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import countriesData from "@/data/countries.json";
import socialMediaAccount from "@/data/socialmedia.json";
import eventBus from "@/utils/eventBus";
export default {
  name: "GeneralSettings",
  data() {
    return {
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
      storeFormData: {
        physicalAddressValue: "",
        storeCountryValue: "",
        storeStateValue: "",
        storeWhatsappValue: "",
        storeCurrencyValue: "",
        storeVisibilityValue: false,
        socialAccounts: [],
        storeVat: {
          status: false,
          value: 7.5,
        },
        paystack: {
          status: false,
          key: "",
          secret: "",
        },
        hisellPay: {
          status: false,
          bank: "",
          account: "",
          name: "",
          subAccountCode: "",
        },
        whatsAppOrder: {
          status: false,
          number: "",
        },
        deliveryLocations: [],
        doorstepDelivery: {
          status: false,
          value: "",
        },
        payonDelivery: {
          status: false,
          value: "",
        },
        parkPickup: false,
        storePickup: false,
        headerCodes: [],
      },
      banks: [],
      businessName: "",
      shopAddressError: false,
      storeCountryError: false,
      storeStateError: false,
      statedeliveryLocationError: false,
      payonDeliveryError: false,
      whatsAppNumberError: false,
      storeCurrencyError: false,
      statedeliveryError: false,
      storeVatError: false,
      paystackKeyError: false,
      paystackSecretError: false,
      hisellPayAcccountError: false,
      hisellPayNameError: false,
      whatsAppOrderNumberError: false,
      deliveryLocationError: false,
      hisellPayNameLoading: false,
      subAccountCreated: false,
      isAdmin: false,
      headerCodes: [],
      headerCodeDropdownOpen: false,
      deliveryCountry: "",
      storeCountry: "",
      storeState: "",
      deliveryState: "",
      storeCurrency: "",
      deliveryError: false,
      deliveryCost: "",
      statedeliveryCost: "",
      socialError: false,
      headerCodeError: false,
      socialDropdownOpen: false,
      doorstepDeliveryError: false,
      addingStateException: false,
      selectedSocial: [],
      selectedHeaderType: "",
      selectedHeaderValue: "",
      selectedSocialValue: "",
      currency: "",
      isCategoryFocused: false,
      isCountryFocused: false,
      isStateFocused: false,
      isDeliveryFocused: false,
      isDeliveryStateFocused: false,
      skipAccountWatch: true,
      showPaystackSecret: false,
      editedFields: {},
      countries: [],
      storeStates: [],
      deliveryStates: [],
      currencies: [],
      generalDetails: null,
      currencySymbol: "",
      savedCountry: [],
      savedState: [],
      socialMediaAccounts: socialMediaAccount,
      loadingGeneralDetails: false,
      regex: /^\+?[1-9]\d{7,14}$/,
      showModal: false,
      videoURL: "https://www.youtube.com/watch?v=nel2b105sxE",
    };
  },
  provide() {
    return {
      helpVideoURL: this.videoURL,
    };
  },
  beforeUnmount() {
    eventBus.off("open-help-video", this.handleOpenVideo);
  },
  created() {
    if (this.storeFormData.storeCountryValue)
      this.handleStoreStates(
        this.countries.find(
          (country) => country.name === this.storeFormData.storeCountryValue
        ).code
      );
  },
  watch: {
    // Watch for both bank and account number changes
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchCountries();
        this.fetchGeneralDetails();
        this.fetchBanks();
        if (this.storeFormData.storeCountryValue)
          this.handleStoreStates(
            this.countries.find(
              (country) => country.name === this.storeFormData.storeCountryValue
            ).code
          );
      },
    },
    "storeFormData.hisellPay.account"(newVal) {
      if (this.skipAccountWatch) return;
      if (newVal.length === 10 && this.storeFormData.hisellPay.bank) {
        this.resolveAccountName();
      }
    },
    "storeFormData.hisellPay.bank"(newVal) {
      if (this.skipAccountWatch) return;
      if (this.storeFormData.hisellPay.account.length === 10 && newVal) {
        this.resolveAccountName();
      }
    },
    storeCountry(newCountry) {
      this.handleStoreStates(newCountry.code);
      this.handleEditedField(true, "storeCountryValue");
      this.storeFormData.storeCountryValue = newCountry.name;
    },
    storeState(newState) {
      this.handleEditedField(true, "storeStateValue");
      this.storeFormData.storeStateValue = newState.name;
    },
    deliveryCountry(newCountry) {
      this.handleEditedField(true, "deliveryLocationValue");
      this.handleDeliveryStates(newCountry.code);
    },
    deliveryState(newState) {
      this.handleEditedField(true, "deliveryLocationValue");
      console.log(newState);
    },
    storeCurrency(newCurrency) {
      this.storeFormData.storeCurrencyValue = newCurrency;
      this.handleEditedField(true, "storeCurrencyValue");
      this.currencySymbol = this.getCurrencySymbol(newCurrency);
    },
  },
  mounted() {
    this.fetchCountries();
    this.fetchGeneralDetails();
    this.fetchBanks();
    eventBus.on("open-help-video", this.handleOpenVideo);
  },
  computed: {
    percentage() {
      return Math.round(
        (100 / this.limit) * this.editor.storage.characterCount.characters()
      );
    },
  },
  methods: {
    ...mapActions([
      "getGeneralDetails",
      "saveGeneralSettings",
      "getPaystackBanks",
      "resolveBankAccount",
      "createPaystackSubAccount",
    ]),
    handleOpenVideo() {
      this.showModal = true;
    },
    getCurrencySymbol(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_symbol : null;
      }
    },
    getCurrencyCode(id) {
      if (id) {
        const currency = this.currencies.find((currency) => currency.id === id);
        return currency ? currency.currency_code : null;
      }
    },
    async fetchGeneralDetails() {
      this.loadingGeneralDetails = true;
      try {
        const response = await this.getGeneralDetails();
        if (response.success) {
          this.generalDetails = response.value[0];
          this.currencies = response.currencies;
          this.isAdmin = this.generalDetails?.isAdmin;

          // Map data correctly from generalDetails
          this.storeFormData.physicalAddressValue =
            this.generalDetails?.physical_address || "";
          this.businessName = this.generalDetails?.store_name || "";
          this.storeFormData.storeCountryValue =
            this.generalDetails?.business_location?.country || "";
          this.storeFormData.storeStateValue =
            this.generalDetails?.business_location?.state || "";
          this.storeFormData.storeWhatsappValue =
            this.generalDetails?.whatsapp_number || "";
          this.storeFormData.storeCurrencyValue =
            this.generalDetails?.default_currency || 1;

          // Convert under_construction to boolean value
          this.storeFormData.storeVisibilityValue = Boolean(
            this.generalDetails?.under_construction
          );

          console.log(
            "Store Form Data",
            this.storeFormData.storeVisibilityValue
          );
          console.log("from web", this.generalDetails?.under_construction);

          // Parse social media accounts, safely handling any parsing errors
          this.storeFormData.socialAccounts = this.generalDetails?.social_media
            ? Array.isArray(this.generalDetails.social_media)
              ? this.generalDetails.social_media
              : JSON.parse(this.generalDetails.social_media)
            : [];

          // VAT information
          this.storeFormData.storeVat = {
            status: Boolean(this.generalDetails?.vat?.status) || false,
            value: this.generalDetails?.vat?.value || "7.5",
          };

          // Payment systems: Handle each separately and ensure correct defaults
          this.storeFormData.paystack = {
            status:
              Boolean(this.generalDetails?.payment_system?.paystack?.status) ||
              false,
            key: this.generalDetails?.payment_system?.paystack?.key || "",
            secret: this.generalDetails?.payment_system?.paystack?.secret || "",
          };

          this.storeFormData.hisellPay = {
            status:
              Boolean(this.generalDetails?.payment_system?.hisellPay?.status) ||
              false,
            bank: this.generalDetails?.payment_system?.hisellPay?.bank || "",
            account:
              this.generalDetails?.payment_system?.hisellPay?.account || "",
            name: this.generalDetails?.payment_system?.hisellPay?.name || "",
            subAccountCode:
              this.generalDetails?.payment_system?.hisellPay?.subAccountCode ||
              "",
            subAccountCreated: this.generalDetails?.payment_system?.hisellPay
              ?.subAccountCode
              ? true
              : false,
          };

          this.storeFormData.whatsAppOrder = {
            status:
              Boolean(
                this.generalDetails?.payment_system?.whatsAppOrder?.status
              ) || false,
            number:
              this.generalDetails?.payment_system?.whatsAppOrder?.number || "",
          };

          // Shipping details: handle locations and other shipping data
          this.storeFormData.deliveryLocations =
            this.generalDetails?.shipping_details?.deliveryLocations || [];

          // Delivery options
          this.storeFormData.doorstepDelivery = {
            status:
              Boolean(
                this.generalDetails?.shipping_details?.doorstepDelivery?.status
              ) || false,
            value:
              this.generalDetails?.shipping_details?.doorstepDelivery?.value ||
              0,
          };

          this.storeFormData.payonDelivery = {
            status:
              Boolean(
                this.generalDetails?.shipping_details?.payonDelivery?.status
              ) || false,
            value:
              this.generalDetails?.shipping_details?.payonDelivery?.value || 0,
          };

          this.storeFormData.parkPickup =
            Boolean(this.generalDetails?.shipping_details?.parkPickup) || false;
          this.storeFormData.storePickup =
            Boolean(this.generalDetails?.shipping_details?.storePickup) ||
            false;

          // Handle header codes: If it is null, default to empty array
          this.storeFormData.headerCodes =
            this.generalDetails?.header_codes || [];

          // Set currency symbol based on the selected currency
          this.currencySymbol =
            this.currencies.find(
              (currency) =>
                currency.id === this.storeFormData.storeCurrencyValue
            )?.currency_symbol || null;
        }
      } catch (error) {
        console.log("There has been an error: ", error);
      } finally {
        this.loadingGeneralDetails = false;
        this.$nextTick(() => {
          this.skipAccountWatch = false;
        });
      }
    },
    toggleWhatsAppNumber(event) {
      if (event.target.checked) {
        this.storeFormData.whatsAppOrder.number =
          this.storeFormData.storeWhatsappValue;
      } else {
        this.storeFormData.whatsAppOrder.number = "";
      }
    },
    resetForm() {
      Object.keys(this.storeFormData).forEach((key) => {
        const keyType = typeof this.storeFormData[key];

        if (keyType === "string") {
          this.storeFormData[key] = "";
        } else if (keyType === "number") {
          this.storeFormData[key] = 0;
        } else if (keyType === "boolean") {
          this.storeFormData[key] = false;
        } else if (Array.isArray(this.storeFormData[key])) {
          this.storeFormData[key] = [];
        } else {
          this.storeFormData[key] = null;
        }
      });
      this.fetchStoreDetails();
      this.editedFields = {};
    },
    fetchCountries() {
      try {
        this.countries = countriesData.map((country) => ({
          code: country.code2,
          name: country.name,
          phoneCode: country.dialling_code,
        }));
        console.log(this.countries, "countries");
      } catch (error) {
        console.error("Error loading countries data:", error.message);
      }
    },
    fetchBanks() {
      try {
        this.getPaystackBanks().then((response) => {
          if (response.success) {
            this.banks = response.banks.data;
          } else {
            console.error(
              "Failed to fetch banks:",
              response.error || "Unknown error"
            );
          }
        });
      } catch (error) {
        console.error("Error loading banks data:", error.message);
      }
    },

    async resolveAccountName() {
      this.hisellPayAcccountError = false;
      this.hisellPayNameError = false;
      this.hisellPayNameLoading = true;

      const bankCode = this.banks.find(
        (b) =>
          b.name.toLowerCase() ===
          this.storeFormData.hisellPay.bank.toLowerCase()
      )?.code;

      if (!bankCode) {
        this.hisellPayAcccountError = true;
        this.hisellPayNameLoading = false;
        return;
      }

      try {
        const response = await this.resolveBankAccount({
          accountNumber: this.storeFormData.hisellPay.account,
          bankCode,
        });

        if (response.success) {
          this.storeFormData.hisellPay.name = response.accountName;
        } else {
          this.hisellPayAcccountError = true;
          this.hisellPayNameError = true;
          console.error("Failed to resolve account name:", response.error);
        }
      } catch (error) {
        this.hisellPayAcccountError = true;
        this.hisellPayNameError = true;
        console.error("Error resolving account name:", error);
      } finally {
        this.hisellPayNameLoading = false; // Stop loading
      }
    },
    async createSubAccount() {
      try {
        const subAccountResponse = await this.createPaystackSubAccount({
          businessName: this.businessName,
          businessEmail: this.generalDetails?.store_email,
          accountNumber: this.storeFormData.hisellPay.account,
          bankCode: this.banks.find(
            (b) =>
              b.name.toLowerCase() ===
              this.storeFormData.hisellPay.bank.toLowerCase()
          )?.code,
          accountName: this.storeFormData.hisellPay.name,
          splitPercentage: 1.7,
        });

        if (subAccountResponse.success) {
          this.storeFormData.hisellPay = {
            ...this.storeFormData.hisellPay,
            subAccountCode: subAccountResponse.subaccountCode,
          };
          toast.success("Account linked successfully!");
          this.storeFormData.hisellPay.subAccountCreated = true;
        } else {
          console.error(
            "Failed to create sub-account:",
            subAccountResponse.error
          );
          toast.error(subAccountResponse.error);
        }
      } catch (error) {
        console.error("Error creating sub-account:", error);
        toast.error("An error occurred while creating the sub-account.");
      }
    },
    handleDeliveryStates(Code) {
      this.deliveryStates = this.getStatesForCountry(Code);
      console.log(this.deliveryStates, "deliverystate");
    },
    handleStoreStates(Code) {
      this.storeStates = this.getStatesForCountry(Code);
    },
    getPhoneCode(country) {
      const countryData = this.countries.find((c) => c.name === country);
      return countryData ? countryData.phoneCode : "";
    },
    getStatesForCountry(countryCode) {
      try {
        if (!countryCode) {
          this.states = [];
          return;
        }

        const selectedCountry = countriesData.find(
          (country) => country.code2 === countryCode
        );
        console.log(selectedCountry, "just got the country");
        return (
          selectedCountry?.states?.map((state) => ({
            code: state.code,
            name: state.name,
          })) || []
        );
      } catch (error) {
        console.error("Error fetching states:", error.message);
      }
    },
    handleEditedField(value, field, subField = null) {
      if (!this.editedFields) {
        this.editedFields = {};
      }
      if (subField) {
        if (!this.editedFields[field]) {
          this.editedFields[field] = {};
        }
        Object.assign(this.editedFields[field], { [subField]: value });
      } else {
        Object.assign(this.editedFields, { [field]: value });
      }
      console.log(this.editedFields, "editedFields");
    },
    addDeliveryLocation() {
      if (this.deliveryCost && this.deliveryCountry) {
        this.storeFormData.deliveryLocations.push({
          type: "country",
          location: this.deliveryCountry.name,
          cost: this.deliveryCost,
        });
        this.handleEditedField(true, "deliveryLocations");
        this.deliveryCost = "";
        this.deliveryCountry = "";
        this.deliveryError = false;
      } else {
        this.deliveryError = true;
      }
    },
    addstateException() {
      if (this.deliveryCountry === "") {
        this.deliveryLocationError = true;
        toast.warning("Please select a country first");
        return;
      }
      this.addingStateException = true;
    },
    addDeliveryLocationState() {
      if (this.deliveryState && this.statedeliveryCost) {
        this.storeFormData.deliveryLocations.push({
          type: "state",
          country: this.deliveryCountry.name,
          location: this.deliveryState,
          cost: this.statedeliveryCost,
        });
        this.handleEditedField(true, "deliveryLocations");
        this.statedeliveryCost = "";
        this.deliveryState = "";
        this.statedeliveryError = false;
      } else {
        this.statedeliveryError = true;
      }
    },
    goback() {
      this.$router.back();
    },
    async handleSubmitForm() {
      const formData = new FormData();

      if (this.editedFields.paystack) {
        if (
          this.storeFormData.paystack.key === "" ||
          this.storeFormData.paystack.secret === ""
        ) {
          this.storeFormData.paystack.status = false;
          toast.warning(
            "Paystack key and secret cannot be empty. Disabling Paystack."
          );
        }
        this.storeFormData.paystack.status = this.storeFormData.paystack.status
          ? 1
          : 0;
      }

      if (this.editedFields.hisellPay) {
        this.storeFormData.hisellPay.status = this.storeFormData.hisellPay
          .status
          ? 1
          : 0;
      }

      if (this.editedFields.whatsAppOrder) {
        this.storeFormData.whatsAppOrder.status = this.storeFormData
          .whatsAppOrder.status
          ? 1
          : 0;
      }

      if (this.editedFields.doorstepDelivery) {
        this.storeFormData.doorstepDelivery.status = this.storeFormData
          .doorstepDelivery.status
          ? 1
          : 0;
      }

      if (this.editedFields.payonDelivery) {
        this.storeFormData.payonDelivery.status = this.storeFormData
          .payonDelivery.status
          ? 1
          : 0;
      }

      if (this.editedFields.parkPickup) {
        this.storeFormData.parkPickup = this.storeFormData.parkPickup ? 1 : 0;
      }

      if (this.editedFields.storePickup) {
        this.storeFormData.storePickup = this.storeFormData.storePickup ? 1 : 0;
      }

      const businessLocationFields = ["storeCountryValue", "storeStateValue"];
      const paymentSystemFields = ["paystack", "hisellPay", "whatsAppOrder"];
      const shippingDetailsFields = [
        "deliveryLocations",
        "doorstepDelivery",
        "payonDelivery",
        "parkPickup",
        "storePickup",
      ];

      // Helper function to check if any child field in a group has been edited
      function isFieldEdited(fields) {
        return fields.some((field) => field in this.editedFields);
      }

      // Append parent groups if any child is edited
      if (isFieldEdited.call(this, businessLocationFields)) {
        formData.append(
          "businessLocation",
          JSON.stringify({
            country: this.storeFormData.storeCountryValue,
            state: this.storeFormData.storeStateValue,
          })
        );
      }

      if (isFieldEdited.call(this, paymentSystemFields)) {
        formData.append(
          "paymentSystem",
          JSON.stringify({
            paystack: this.storeFormData.paystack,
            hisellPay: this.storeFormData.hisellPay,
            whatsAppOrder: this.storeFormData.whatsAppOrder,
          })
        );
      }

      if (isFieldEdited.call(this, shippingDetailsFields)) {
        formData.append(
          "shippingDetails",
          JSON.stringify({
            deliveryLocations: this.storeFormData.deliveryLocations,
            doorstepDelivery: this.storeFormData.doorstepDelivery,
            payonDelivery: this.storeFormData.payonDelivery,
            parkPickup: this.storeFormData.parkPickup,
            storePickup: this.storeFormData.storePickup,
          })
        );
      }

      if (this.editedFields.storeVat) {
        const vatstatus = this.storeFormData.storeVat.status ? 1 : 0;

        this.storeFormData.storeVat.status = vatstatus;
      }

      // Append standalone fields (not part of any parent group)
      for (const field in this.editedFields) {
        if (
          !businessLocationFields.includes(field) &&
          !paymentSystemFields.includes(field) &&
          !shippingDetailsFields.includes(field)
        ) {
          if (typeof this.storeFormData[field] === "object") {
            formData.append(field, JSON.stringify(this.storeFormData[field]));
          } else if (typeof this.storeFormData[field] === "boolean") {
            formData.append(field, this.storeFormData[field] ? 1 : 0);
          } else if (
            this.storeFormData[field] !== null &&
            this.storeFormData[field] !== undefined
          ) {
            formData.append(field, this.storeFormData[field]);
          }
        }
      }

      // Log FormData content for debugging
      for (const [key, value] of formData.entries()) {
        if (value instanceof File) {
          console.log(`Key: ${key}, Value:`, {
            name: value.name,
            size: value.size,
            type: value.type,
          });
        } else {
          console.log(`Key: ${key}, Value:`, value);
        }
      }

      // Submit the form data
      try {
        const generalSettingsPromise = this.saveGeneralSettings(formData).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Update Failed!"));
            }

            if (this.editedFields.paystack) {
              this.storeFormData.paystack.status = Boolean(
                this.storeFormData.paystack.status
              );
            }

            if (this.editedFields.hisellPay) {
              this.storeFormData.hisellPay.status = Boolean(
                this.storeFormData.hisellPay.status
              );
            }

            if (this.editedFields.whatsAppOrder) {
              this.storeFormData.whatsAppOrder.status = Boolean(
                this.storeFormData.whatsAppOrder.status
              );
            }

            if (this.editedFields.doorstepDelivery) {
              this.storeFormData.doorstepDelivery.status = Boolean(
                this.storeFormData.doorstepDelivery.status
              );
            }

            if (this.editedFields.payonDelivery) {
              this.storeFormData.payonDelivery.status = Boolean(
                this.storeFormData.payonDelivery.status
              );
            }

            if (this.editedFields.parkPickup) {
              this.storeFormData.parkPickup = Boolean(
                this.storeFormData.parkPickup
              );
            }

            if (this.editedFields.storePickup) {
              this.storeFormData.storePickup = Boolean(
                this.storeFormData.storePickup
              );
            }

            if (this.editedFields.storeVat) {
              this.storeFormData.storeVat.status = Boolean(
                this.storeFormData.storeVat.status
              );
            }

            this.editedFields = {};
            return data.response;
          }
        );

        toast.promise(
          generalSettingsPromise,
          {
            pending: "Updating settings...",
            success: {
              render() {
                return "Settings updated successfully!";
              },
              icon: true,
            },
            error: {
              render({ data }) {
                return data || "Failed to update settings!";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error("There has been an error: ", error);
        toast.error("Failed to update settings!");
      }
    },
    handleFocus(type = "discription") {
      if (type === "category") this.isCategoryFocused = true;
      if (type === "country") this.isCountryFocused = true;
      if (type === "state") this.isStateFocused = true;
      if (type === "delivery") this.isDeliveryFocused = true;
      if (type === "stateexception") this.isDeliveryStateFocused = true;
      this.isDescriptionFocused = true;
    },
    handleBlur(type = "discription") {
      if (type === "category") this.isCategoryFocused = false;
      if (type === "country") this.isCountryFocused = false;
      if (type === "state") this.isStateFocused = false;
      if (type === "delivery") this.isDeliveryFocused = false;
      if (type === "stateexception") this.isDeliveryStateFocused = false;
      this.isDescriptionFocused = false;
    },
    toggleMenu() {
      this.isActive = !this.isActive;
    },
    deleteSocial(index) {
      if (this.storeFormData.socialAccounts.length > 0)
        this.storeFormData.socialAccounts.splice(index, 1);
      this.handleEditedField(true, "socialAccounts");
    },
    toggleSocialDropDown() {
      this.socialDropdownOpen = !this.socialDropdownOpen;
    },
    toggleHeaderCodeDropdown() {
      this.headerCodeDropdownOpen = !this.headerCodeDropdownOpen;
    },
    socialOption(option) {
      this.selectedSocial = option;
      this.socialDropdownOpen = false;
    },
    headerCodeOption(option) {
      this.selectedHeaderType = option;
      this.headerCodeDropdownOpen = false;
    },
    addHeaderCode() {
      if (this.selectedHeaderType && this.selectedHeaderValue) {
        this.headerCodes.push({
          type: this.selectedHeaderType,
          code: this.selectedHeaderValue,
        });
        this.selectedHeaderType = "";
        this.selectedHeaderValue = "";
        this.headerCodeError = false;
      } else {
        this.headerCodeError = true;
      }
    },
    deleteHeaderCode(index) {
      if (this.headerCodes.length > 0) this.headerCodes.splice(index, 1);
    },
    deleteLocation(index) {
      if (confirm("Are you sure you want to delete this location?")) {
        this.storeFormData.deliveryLocations.splice(index, 1);
      }
    },
    addSocial() {
      console.log(this.storeFormData.socialAccounts);
      if (this.selectedSocial && this.selectedSocialValue) {
        const social = {
          socialMedia: this.selectedSocial?.name || "",
          handle: this.selectedSocialValue || "",
          link:
            `https://${this.selectedSocial?.linkPrefix}${this.selectedSocialValue}` ||
            "",
        };
        this.storeFormData.socialAccounts.push(social);
        console.log(this.storeFormData.socialAccounts);
        this.handleEditedField(true, "socialAccounts");
        this.selectedSocial = null;
        this.selectedSocialValue = "";
        this.socialError = false;
      } else {
        this.socialError = true;
      }
    },
    selectOption(option) {
      this.selectedOption = option;
      this.isActive = false;
    },
  },
};
</script>
